import React from 'react';

// ── Shared style tokens ───────────────────────────────────────────────────────
export const DB_STYLE = {
  dateInput: {
    background: '#1e293b',
    color: '#cbd5e1',
    border: '1px solid #334155',
    borderRadius: 5,
    fontSize: 12,
    padding: '3px 7px',
    colorScheme: 'dark',
  },
  btnMd: {
    background: '#1e3a5f',
    color: '#93c5fd',
    border: '1px solid #1e40af',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    padding: '5px 10px',
    cursor: 'pointer',
  },
  btnCsv: {
    background: '#14281a',
    color: '#86efac',
    border: '1px solid #166534',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    padding: '5px 10px',
    cursor: 'pointer',
  },
};

// ── Stat tile ─────────────────────────────────────────────────────────────────
// Supports optional `sub` for a secondary info line below the label.
export function DashTile({ label, value, color, sub, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#1e293b', borderRadius: 10, padding: '14px 18px',
        flex: '1 1 130px', minWidth: 120, borderTop: `3px solid ${color}`,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: '#475569', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────
export function DashSection({ label }) {
  return (
    <div style={{
      fontSize: 10, color: '#475569', fontWeight: 700, letterSpacing: 1,
      marginBottom: 8, marginTop: 18, paddingBottom: 4, borderBottom: '1px solid #0f172a',
    }}>
      {label}
    </div>
  );
}

// ── Segmented bar ─────────────────────────────────────────────────────────────
// items: [{ value|key, label, count, color? }]
// Accepts items using either `value` or `key` as the identifier.
// onClick receives the item identifier (value ?? key).
const PALETTE = ['#22c55e', '#0ea5e9', '#f59e0b', '#a855f7', '#f97316', '#ec4899', '#64748b'];

export function DashSegBar({ items, total, onClick }) {
  if (!items || !items.length) return null;
  const t = total || items.reduce((a, i) => a + i.count, 0) || 1;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', height: 14, borderRadius: 6, overflow: 'hidden', marginBottom: 6 }}>
        {items.map((it, i) => {
          const k = it.value ?? it.key ?? it.label;
          return (
            <div
              key={k}
              onClick={() => onClick && onClick(it.value ?? it.key)}
              title={`${it.label}: ${it.count}`}
              style={{
                width: `${(it.count / t) * 100}%`,
                background: it.color || PALETTE[i % PALETTE.length],
                cursor: onClick ? 'pointer' : 'default',
                transition: 'opacity 0.15s',
              }}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {items.map((it, i) => {
          const k = it.value ?? it.key ?? it.label;
          const c = it.color || PALETTE[i % PALETTE.length];
          return (
            <button
              key={k}
              onClick={() => onClick && onClick(it.value ?? it.key)}
              style={{
                background: 'none', border: 'none', padding: 0,
                cursor: onClick ? 'pointer' : 'default',
                fontSize: 11, color: c, display: 'flex', gap: 4, alignItems: 'center',
              }}
            >
              <span style={{ fontWeight: 700 }}>● {it.label}</span>
              <span style={{ color: '#475569' }}>×{it.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Filter chip bar ─────────────────────────────────────────────────────────────
// chips: Array<{ label, color?, onRemove? }>
// hint:  optional right-hand count text
// onClearAll: shown when chips.length > 1
export function FilterChipBar({ chips, hint, onClearAll }) {
  if (!chips || !chips.length) return null;
  return (
    <div style={{
      display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
      marginBottom: 10, padding: '7px 12px',
      background: '#0f172a', borderRadius: 8, border: '1px solid #1e293b',
    }}>
      <span style={{ fontSize: 10, color: '#475569', fontWeight: 700, letterSpacing: 0.8, flexShrink: 0 }}>FILTER</span>
      {chips.map((chip, i) => {
        const col = chip.color || '#64748b';
        return (
          <span key={i} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700,
            background: col + '22', color: col, border: `1px solid ${col}55`,
            borderRadius: 20, padding: '3px 8px 3px 10px',
          }}>
            {chip.label}
            {chip.onRemove && (
              <button onClick={chip.onRemove} style={{
                background: 'none', border: 'none', color: col, cursor: 'pointer',
                fontSize: 14, lineHeight: 1, padding: '0 0 0 2px', opacity: 0.8,
              }}>×</button>
            )}
          </span>
        );
      })}
      {hint && <span style={{ fontSize: 11, color: '#475569' }}>{hint}</span>}
      {onClearAll && chips.length > 1 && (
        <button onClick={onClearAll} style={{
          background: 'none', border: '1px solid #1e293b', borderRadius: 20,
          color: '#334155', cursor: 'pointer', fontSize: 10, padding: '2px 10px', marginLeft: 'auto',
        }}>Clear all ×</button>
      )}
    </div>
  );
}

// ── File download utility ─────────────────────────────────────────────────────
export function downloadFile(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
